/**
 * Represents a single node within the tree structure.
 * @template T - The type of the payload data stored in the node.
 */
export interface Node<T> {
  /** Uniquely identifies the node within the tree. */
  readonly key: string;
  /** The payload data associated with this node. */
  data: T;
  /** The key of the parent node. Undefined if the node is a root. */
  parent?: string;
  /** The zero-based position of this node relative to its siblings. */
  index: number;
  /** An ordered array of keys representing the children of this node. */
  children: string[];
}

/**
 * Recursively yields nodes from the given keys and their subtrees.
 * Utilizes depth-first traversal.
 * @template T - The type of the payload data.
 * @param tree - The read-only tree view to traverse.
 * @param keys - The initial keys to start traversing from.
 * @returns A generator yielding nodes sequentially.
 */
export function* enumerateSubTrees<T>(tree: TreeView<T>, keys: readonly string[]): Generator<Node<T>, void, unknown> {
  for (const nodeKey of keys) {
    const node = tree.getNode(nodeKey);
    yield node;
    yield* enumerateSubTrees(tree, node.children);
  }
}

/**
 * Enumerates all nodes in the tree starting from the roots.
 * @template T - The type of the payload data.
 * @param tree - The read-only tree view to traverse.
 * @returns A generator yielding all nodes in the tree.
 */
export const enumerateTree = <T>(tree: TreeView<T>) => enumerateSubTrees(tree, tree.roots);

/**
 * A read-only projection of a tree data structure.
 * Ideal for rendering or querying the tree without allowing mutations.
 * @template T - The type of the payload data stored in the nodes.
 */
export interface TreeView<T> {
  /** An ordered list of keys representing the root nodes of the tree. */
  roots: readonly string[];
  /**
   * Attempts to retrieve a node by its key.
   * @param key - The unique identifier of the node.
   * @returns A read-only version of the node, or undefined if not found.
   */
  tryGetNode(key: string): Readonly<Node<T>> | undefined;
  /**
   * Retrieves a node by its key.
   * @param key - The unique identifier of the node.
   * @returns A read-only version of the node.
   * @throws {Error} If the node with the specified key does not exist.
   */
  getNode(key: string): Readonly<Node<T>>;
}
